/* ==========================================================================
   streaky.js — shared behaviour for the Streaky Audio st-* sections
   --------------------------------------------------------------------------
   Event-delegated so it works no matter which sections are on the page.
   Cart actions reuse Shopify's AJAX cart (native pixel events keep firing):
     [data-st-add]  -> /cart/add.js then go to the cart
     [data-st-buy]  -> /cart/add.js then go straight to /checkout
   Plus: lazy testimonial video, specs accordion, style tabs, dismissible
   announcement.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__streakyInit) return;       // guard against double-load
  window.__streakyInit = true;

  var ROOT = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/+$/, '');

  function setLoading(btn, on) {
    if (!btn) return;
    btn.classList.toggle('is-loading', !!on);
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
    if (on) { btn.disabled = true; } else { btn.disabled = false; }
  }

  function cartAdd(variantId) {
    return fetch(ROOT + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    }).then(function (r) { if (!r.ok) throw r; return r.json(); });
  }

  function flashAdded(btn) {
    if (!btn) return;
    if (btn.getAttribute('data-st-orig') == null) btn.setAttribute('data-st-orig', btn.textContent);
    btn.textContent = 'Added ✓';
    btn.classList.add('is-added');
    clearTimeout(btn._stAddedT);
    btn._stAddedT = setTimeout(function () {
      btn.textContent = btn.getAttribute('data-st-orig');
      btn.classList.remove('is-added');
    }, 1800);
  }

  function handleAdd(variantId, btn, checkout) {
    if (!variantId) return;
    setLoading(btn, true);
    cartAdd(variantId)
      .then(function () {
        // Every add (Add to Cart and Buy Now) routes the shopper to the add-ons
        // page so they complete their setup before checkout. Bundle reconcile
        // (cart-bundle-rules.js) runs on that page's load.
        window.location.href = ROOT + '/pages/complete-your-setup';
      })
      .catch(function () { window.location.href = ROOT + '/cart'; });
  }

  function playVideo(trigger) {
    var wrap = trigger.closest('[data-st-video-wrap]') || trigger.parentNode;
    var src = trigger.getAttribute('data-src');
    if (!src || !wrap) return;
    var v = document.createElement('video');
    v.src = src;
    v.controls = true;
    v.autoplay = true;
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;background:#000;';
    // pause any other playing video first
    document.querySelectorAll('.st video').forEach(function (other) { try { other.pause(); } catch (e) {} });
    wrap.appendChild(v);
    wrap.classList.add('is-playing');
    v.play().catch(function () {});
  }

  /* Owner upgrade: add the plugin, then go to the add-ons page.

     Deliberately a POST to /cart/add rather than the /cart/<id>:1 permalink.
     The permalink adds the item and then sends the visitor to CHECKOUT, and its
     return_to is not honoured as a destination; only the POST path lets us
     choose where they land. Rendered as a <button> only for signed-in
     customers, so there is no logged-out case to handle here. */
  function handleUpgrade(el) {
    var id = el.getAttribute('data-variant-id');
    var back = el.getAttribute('data-return-to') || '/pages/complete-your-setup';
    if (!id) return;
    el.setAttribute('aria-busy', 'true');
    cartAdd(id).then(function () {
      window.location.href = ROOT + back;
    }).catch(function () {
      /* Leave them on the page with the control usable rather than bouncing
         them somewhere confusing; the normal Add to cart still works. */
      el.setAttribute('aria-busy', 'false');
    });
  }

  /* Finish an upgrade that started before sign-in.

     The signed-out control sends the visitor to
     /customer_authentication/login?return_to=<this page>?upgrade=1. When they
     come back the page is rendered for a signed-in customer, so the control is
     now a [data-st-upgrade] button; its presence IS the proof they are signed
     in, which is why nothing here re-checks. Fire it once and strip the flag
     from the URL first, so a refresh or a back-button cannot add a second copy. */
  function resumeUpgrade() {
    if (!/[?&]upgrade=1(&|$)/.test(window.location.search)) return;
    var el = document.querySelector('[data-st-upgrade]');
    try {
      var clean = window.location.pathname + window.location.search
        .replace(/([?&])upgrade=1(&|$)/, '$1').replace(/[?&]$/, '');
      window.history.replaceState({}, '', clean + window.location.hash);
    } catch (e) {}
    if (el) handleUpgrade(el);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', resumeUpgrade);
  else resumeUpgrade();

  function toggleAccordion(trigger) {
    var item = trigger.closest('[data-st-accordion-item]');
    if (!item) return;
    var open = item.classList.toggle('is-open');
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    var panel = item.querySelector('[data-st-accordion-panel]');
    if (panel) panel.hidden = !open;
  }

  /* ---- Style tabs (st-style-tabs): swap image + copy, keyboard accessible ---- */
  function tabsIn(el) {
    var bar = el.closest('[data-st-tabs]');
    return bar ? [].slice.call(bar.querySelectorAll('[data-st-tab]')) : [];
  }

  /* A tab panel may hold a video (st-style-tabs blocks with a video_url). Leaving
     that tab has to STOP AND RELEASE it: pausing alone leaves a buffered <video>
     in the DOM still holding the download, and a hidden panel that keeps its
     audio is the worst version of this. Clearing src + load() cancels the fetch
     before the element is removed.
     No-op for every tab set without video, which is all of them except the
     BB2 VOX/DRUMS/MIX instance. */
  function releaseTabVideos(root) {
    root.querySelectorAll('[data-st-video-wrap] video').forEach(function (v) {
      try { v.pause(); } catch (e) {}
      try { v.removeAttribute('src'); v.load(); } catch (e) {}
      if (v.parentNode) v.parentNode.removeChild(v);
    });
    root.querySelectorAll('[data-st-video-wrap].is-playing').forEach(function (w) {
      w.classList.remove('is-playing');          // bring the poster back
    });
  }

  function activateTab(tab, focus) {
    var root = tab.closest('[data-st-tabs-root]');
    if (!root) return;
    var idx = tab.getAttribute('data-st-tab');
    /* Only when the tab actually changes. Clicking the tab you are already on
       must not kill the video you just started. */
    if (tab.getAttribute('aria-selected') !== 'true') releaseTabVideos(root);
    tabsIn(tab).forEach(function (t) {
      var on = t === tab;
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.setAttribute('tabindex', on ? '0' : '-1');
    });
    root.querySelectorAll('[data-st-tabpanel]').forEach(function (p) {
      p.hidden = p.getAttribute('data-st-tabpanel') !== idx;
    });
    if (focus) tab.focus();
  }

  function moveTab(tab, delta) {
    var tabs = tabsIn(tab);
    var i = tabs.indexOf(tab);
    if (i < 0 || !tabs.length) return;
    activateTab(tabs[(i + delta + tabs.length) % tabs.length], true);
  }

  function dismiss(el) {
    var bar = el.closest('[data-st-announcement]');
    if (!bar) return;
    bar.hidden = true;
    try { localStorage.setItem('st-announce-dismissed', bar.getAttribute('data-key') || '1'); } catch (e) {}
  }

  // Hide a previously-dismissed announcement on load.
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-st-announcement]').forEach(function (bar) {
      try {
        if (localStorage.getItem('st-announce-dismissed') === (bar.getAttribute('data-key') || '1')) bar.hidden = true;
      } catch (e) {}
    });
  });

  // Guarantee muted autoplay videos (hero/feature loops) actually start —
  // some browsers defer autoplay even when muted.
  function stStartVideos() {
    document.querySelectorAll('video[autoplay]').forEach(function (vid) {
      vid.muted = true;
      var pr = vid.play();
      if (pr && pr.catch) pr.catch(function () {});
    });
  }
  document.addEventListener('DOMContentLoaded', stStartVideos);
  window.addEventListener('load', stStartVideos);

  // Style tabs: arrow keys move between tabs, Home/End jump to the ends.
  document.addEventListener('keydown', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var tab = t.closest('[data-st-tab]');
    if (!tab) return;
    var k = e.key;
    if (k === 'ArrowRight' || k === 'ArrowDown') { e.preventDefault(); moveTab(tab, 1); }
    else if (k === 'ArrowLeft' || k === 'ArrowUp') { e.preventDefault(); moveTab(tab, -1); }
    else if (k === 'Home') { e.preventDefault(); activateTab(tabsIn(tab)[0], true); }
    else if (k === 'End') { e.preventDefault(); var a = tabsIn(tab); activateTab(a[a.length - 1], true); }
  });

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var add = t.closest('[data-st-add]');
    if (add) { e.preventDefault(); handleAdd(add.getAttribute('data-variant-id'), add, false); return; }
    var buy = t.closest('[data-st-buy]');
    if (buy) { e.preventDefault(); handleAdd(buy.getAttribute('data-variant-id'), buy, true); return; }
    var upg = t.closest('[data-st-upgrade]');
    if (upg) { e.preventDefault(); handleUpgrade(upg); return; }
    var vid = t.closest('[data-st-video]');
    if (vid) { e.preventDefault(); playVideo(vid); return; }
    var acc = t.closest('[data-st-accordion-trigger]');
    if (acc) { e.preventDefault(); toggleAccordion(acc); return; }
    var tab = t.closest('[data-st-tab]');
    if (tab) { e.preventDefault(); activateTab(tab, false); return; }
    var dis = t.closest('[data-st-dismiss]');
    if (dis) { e.preventDefault(); dismiss(dis); return; }
  });
})();
